import { useTenant } from '@plato/app-core/hooks/use-tenant';
import { AutoSaveIndicator } from '@plato/ui-legacy/components/common/auto-save-indicator';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { useIsMutating } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageTitle } from '../../../components/page-title.tsx';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import { useCatalog } from '../../catalog/components/global-catalog-provider.tsx';
import { DraftSummary } from '../components/draft-summary.tsx';
import { LineItemHeader } from '../components/line-item-header.tsx';
import { ProductLineItemRow } from '../components/product-line-item-row.tsx';
import { RecommendedProducts } from '../components/recommended-products.tsx';
import { ProductSearchInput } from '../form/product-search-input.tsx';
import { useAddProductToDraft } from '../hooks/use-add-product-to-draft.ts';
import { useConfirmDelete } from '../hooks/use-confirm-delete.ts';
import { useDraftContext } from '../hooks/use-draft-context.ts';

export function DraftLineItemsPage(): ReactElement {
    const { t } = useTranslation('sales-draft');
    const { showCatalog } = useCatalog();
    const { draftId, draft } = useDraftContext();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const webshopUrl = useTenant().webshopUrl;

    if (!draftId) {
        // we only get here if static routing is wrong
        throw new Error(`expected route parameter draft_id`);
    }

    const { confirmDelete } = useConfirmDelete();
    const isUpdating = useIsMutating() > 0;

    // Single-open accordion. Track the one expanded row, seeded with the newest line item
    const lastLineItemId = draft?.line_items.findLast((item) => item.normal_line_item)?.line_item_id;
    const [expandedLineItemId, setExpandedLineItemId] = useState(lastLineItemId);

    const { addProduct } = useAddProductToDraft({
        onSuccess: (addedLineItem) => {
            setExpandedLineItemId(addedLineItem.line_item_id);
        },
    });

    // Scroll the newly added line item into view. New items are appended, so the last child of the
    // rows container is the newest row.
    const linesContainerRef = useRef<HTMLDivElement>(null);
    const prevLastLineItemId = useRef(lastLineItemId);
    useEffect(() => {
        const previous = prevLastLineItemId.current;
        prevLastLineItemId.current = lastLineItemId;
        if (previous !== undefined && lastLineItemId !== undefined && previous !== lastLineItemId) {
            linesContainerRef.current?.lastElementChild?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [lastLineItemId]);

    function onAddProductFromCatalog(data: ProductVariantDTO, quantity: number): void {
        addProduct(
            {
                product_variant_id: data.product_variant_id,
                article_group_id: data.article_group_id,
                quantity,
                quantity_unit: data.quantity_unit_sales,
            },
            'catalogue',
        );
    }

    useEffect(() => {
        if (draft && draft.state !== 'NEW') {
            void navigate('/drafts', { replace: true });
        }
    }, [draft, navigate]);

    function handleContinue(): void {
        void navigate({
            pathname: '../header',
            search: searchParams.toString(),
        });
    }

    return (
        <Page className="max-h-dvh overflow-hidden">
            <PageTitle
                title={
                    draft?.type === 'QUOTE'
                        ? t('sales_draft__header__sub_title__quote')
                        : t('sales_draft__header__sub_title__order')
                }
            />
            <PageHeader
                title={
                    draft?.type === 'QUOTE'
                        ? t('sales_draft__header__sub_title__quote')
                        : t('sales_draft__header__sub_title__order')
                }
            >
                <AutoSaveIndicator isUpdating={isUpdating} />
            </PageHeader>
            <div className="flex grow min-h-0 flex-col gap-4 overflow-hidden sm:flex-row">
                <div className="flex grow min-h-0 flex-col gap-4">
                    <div className="flex shrink gap-3 min-w-0">
                        <div className="min-w-0 flex-1">
                            <ProductSearchInput
                                draftId={draftId}
                                customerId={draft?.customer_id}
                                onSelect={(product) => {
                                    addProduct(
                                        {
                                            product_variant_id: product.product_variant_id,
                                            article_group_id: product.article_group_id,
                                        },
                                        'search',
                                    );
                                }}
                            />
                        </div>
                        {webshopUrl && (
                            <Button
                                className="shrink-0"
                                onClick={() => {
                                    void showCatalog(onAddProductFromCatalog);
                                }}
                            >
                                {t('sales_draft__button_group__search_catalog')}
                            </Button>
                        )}
                        {/* TODO: add note line items */}
                    </div>

                    <div className="flex grow min-h-0 flex-col gap-4 overflow-y-auto">
                        {draft && draft.line_items.length > 0 && (
                            <div
                                className="flex shrink-0 flex-col gap-2 text-sm sm:gap-0 sm:overflow-hidden sm:rounded-md sm:border sm:border-neutral-100"
                                role="table"
                            >
                                <LineItemHeader />
                                <div
                                    ref={linesContainerRef}
                                    className="flex flex-col gap-2 overflow-y-auto sm:gap-0"
                                >
                                    {draft.line_items.map((lineItem) =>
                                        lineItem.normal_line_item ? (
                                            <ProductLineItemRow
                                                key={lineItem.line_item_id}
                                                draft={draft}
                                                lineItem={lineItem.normal_line_item}
                                                expanded={expandedLineItemId === lineItem.line_item_id}
                                                onOpenChange={(open) => {
                                                    setExpandedLineItemId(
                                                        open ? lineItem.line_item_id : undefined,
                                                    );
                                                }}
                                                onSelectRelatedProduct={(product) => {
                                                    addProduct(
                                                        {
                                                            product_variant_id: product.product_variant_id,
                                                            article_group_id: product.article_group_id,
                                                        },
                                                        'recommended',
                                                    );
                                                }}
                                            />
                                        ) : undefined,
                                    )}
                                </div>
                            </div>
                        )}

                        {draft && (
                            <RecommendedProducts
                                draft={draft}
                                onSelectProduct={(product) => {
                                    addProduct(
                                        {
                                            product_variant_id: product.product_variant_id,
                                            article_group_id: product.article_group_id,
                                        },
                                        'recommended',
                                    );
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-4 sm:w-80 shrink-0">
                    {draft ? <DraftSummary draft={draft} /> : <div className="grow" />}

                    <div className="flex flex-row gap-4 sm:flex-col-reverse">
                        <Button
                            variant="subtle"
                            className="flex-1"
                            onClick={() => {
                                confirmDelete(draftId);
                            }}
                        >
                            {t('sales_draft__button_group__cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            className="flex-1"
                            disabled={isUpdating || !draft?.line_items.length}
                            onClick={handleContinue}
                        >
                            {t('sales_draft__button_group__continue')}
                        </Button>
                    </div>
                </div>
            </div>
        </Page>
    );
}
